// Thin GA4 wrapper. The gtag bootstrap lives in index.html (same property and
// Consent-Mode defaults as the marketing site), so analytics stay denied in the
// EEA/UK unless the visitor has already opted in on riffloop.app.
//
// Every call is best-effort: if gtag never loaded (blocked, offline, consent
// denied) this is a no-op. Analytics must never be able to break the Studio.

type Params = Record<string, string | number | boolean | undefined>;

type Gtag = (cmd: 'event', name: string, params?: Params) => void;

export function track(event: string, params: Params = {}): void {
  try {
    const g = (window as unknown as { gtag?: Gtag }).gtag;
    if (typeof g !== 'function') return;
    // Drop undefined values so GA4 doesn't record empty params.
    const clean: Params = {};
    for (const [k, v] of Object.entries(params)) if (v !== undefined) clean[k] = v;
    g('event', event, clean);
  } catch {
    /* never throw from telemetry */
  }
}

// Bucket a separation failure into a small, stable set of causes. Deliberately
// mirrors rlSepErrorReason() in the extension's content.js so `reason` means the
// same thing on both surfaces and the two funnels can be compared directly.
// GA4 gets the BUCKET, never the raw message — messages are user-facing prose
// and would blow up cardinality without telling us anything actionable.
export function sepErrorReason(e: unknown): string {
  const err = e as { message?: unknown; name?: string } | null | undefined;
  const msg = err == null ? '' : String(err.message != null ? err.message : err);
  if (!msg) return 'empty';
  if (/timed out|timeout/i.test(msg)) return 'timeout';
  if (/out of memory|allocation failed|Array buffer allocation/i.test(msg)
      || (err && err.name === 'RangeError')) return 'out_of_memory';
  if (/Failed to fetch|NetworkError|net::|ERR_INTERNET|Load failed/i.test(msg)) return 'network';
  if (/produced no stems|no stems/i.test(msg)) return 'no_stems';
  if (/decodeAudioData|Unable to decode|decoding|decode/i.test(msg)) return 'decode';
  if (/\.onnx|model|wasm|webgpu|backend/i.test(msg)) return 'model_load';
  return 'other';
}
